import { styled } from '@mui/material';

interface DynamicLayoutProps {
  titlebar?: React.ReactNode;
  header?: React.ReactNode;
  main: React.ReactNode;
  aside?: React.ReactNode;
}

export const DynamicLayout: React.FC<DynamicLayoutProps> = ({ titlebar, header, main, aside }) => {
  const hasAsideContent = () => {
    if (!aside) return false;
    if (Array.isArray(aside)) {
      return aside.length > 0;
    }
    return true;
  };

  const hasHeaderContent = () => {
    if (!header) return false;
    if (Array.isArray(header)) {
      return header.length > 0;
    }
    return true;
  };

  const hasTitlebarContent = () => {
    if (!titlebar) return false;
    if (Array.isArray(titlebar)) {
      return titlebar.length > 0;
    }
    return true;
  };

  return (
    <BaseWrapper>
      {hasTitlebarContent() && (
        <StyledTitlebar>
          <TitlebarContentWrapper>{titlebar}</TitlebarContentWrapper>
        </StyledTitlebar>
      )}
      <ContentWrapper>
        {hasHeaderContent() && <StyledHeader>{header}</StyledHeader>}
        <LayoutWrapper>
          <StyledMain>{main}</StyledMain>
          {hasAsideContent() && <StyledAside>{aside}</StyledAside>}
        </LayoutWrapper>
      </ContentWrapper>
    </BaseWrapper>
  );
};

const BaseWrapper = styled('div')`
  z-index: 1;
  width: 100%;
`;

const LayoutWrapper = styled('div')`
  display: flex;
  width: 100%;
  justify-content: center;
  gap: 24px;

  @media (max-width: 767px) {
    flex-direction: column-reverse;
  }
`;

const StyledTitlebar = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
  height: var(--app-header-height);
  padding: 10px 24px;
  z-index: 10;
  background-color: var(--mui-palette-background-main);
  border-bottom: 1px solid var(--mui-palette-border-secondary);

  & button {
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
  }
`;

const StyledHeader = styled('div')`
  width: 100%;
  max-width: 840px;
  margin: 0 auto 22px auto;
`;

const StyledMain = styled('div')`
  flex: 1;
  width: 100%;
  max-width: 840px;
  margin: 0 auto;
`;

const StyledAside = styled('div')`
  width: 264px;
  position: sticky;
  top: calc(var(--app-header-height) + 20px);
  align-self: flex-start;
  flex-shrink: 0;

  @media (max-width: 767px) {
    position: static;
    margin: 0 auto;
    width: 100%;
  }
`;

const ContentWrapper = styled('div')`
  margin: 0 auto;
  width: min(1144px, 100%); /* 840px + 264px + 40px gap */
  position: relative;
  padding-bottom: 40px;
  padding-inline: 20px;
  z-index: 1;
`;

const TitlebarContentWrapper = styled('div')`
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  width: 100%;
`;
